import { useState } from 'react'
import { Search, ChevronDown } from 'lucide-react'

const treatmentTypeOptions = [
  'Wellness Exam',
  'Sick Visit',
  'Surgery',
  'Dental',
  'Vaccination',
  'Emergency',
  'Follow-Up',
] as const

const statusOptions = ['Planned', 'In Progress', 'Completed', 'Cancelled'] as const

interface TreatmentSearchProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  typeFilter: string
  onTypeFilterChange: (type: string) => void
  statusFilter: string
  onStatusFilterChange: (status: string) => void
}

export function TreatmentSearch({
  searchQuery,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
}: TreatmentSearchProps) {
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false)
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false)

  return (
    <div data-testid="treatment-search" className="flex items-center gap-3 flex-wrap">
      {/* Search bar */}
      <div className="relative flex-1 min-w-[200px] max-w-[360px]">
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          data-testid="treatment-search-input"
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by patient name, treatment ID, or diagnosis..."
          className="w-full h-7 pl-7 pr-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40 placeholder:text-text-disabled"
        />
      </div>

      {/* Treatment type filter dropdown */}
      <div className="relative">
        <button
          data-testid="treatment-type-filter"
          onClick={() => {
            setTypeDropdownOpen(!typeDropdownOpen)
            setStatusDropdownOpen(false)
          }}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
        >
          <span className="text-[11px] text-text-muted mr-0.5">Type:</span>
          {typeFilter || 'All'}
          <ChevronDown size={12} className="text-text-muted" />
        </button>
        {typeDropdownOpen && (
          <div
            data-testid="treatment-type-filter-dropdown"
            className="absolute z-10 top-full left-0 mt-1 bg-surface border border-border rounded-[4px] min-w-[160px] max-h-48 overflow-y-auto"
            style={{ boxShadow: 'var(--shadow-elevation-1)' }}
          >
            <button
              type="button"
              data-testid="type-option-all"
              onClick={() => {
                onTypeFilterChange('')
                setTypeDropdownOpen(false)
              }}
              className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
            >
              All
            </button>
            {treatmentTypeOptions.map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`type-option-${t}`}
                onClick={() => {
                  onTypeFilterChange(t)
                  setTypeDropdownOpen(false)
                }}
                className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Status filter dropdown */}
      <div className="relative">
        <button
          data-testid="treatment-status-filter"
          onClick={() => {
            setStatusDropdownOpen(!statusDropdownOpen)
            setTypeDropdownOpen(false)
          }}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
        >
          <span className="text-[11px] text-text-muted mr-0.5">Status:</span>
          {statusFilter || 'All'}
          <ChevronDown size={12} className="text-text-muted" />
        </button>
        {statusDropdownOpen && (
          <div
            data-testid="treatment-status-filter-dropdown"
            className="absolute z-10 top-full left-0 mt-1 bg-surface border border-border rounded-[4px] min-w-[160px] max-h-48 overflow-y-auto"
            style={{ boxShadow: 'var(--shadow-elevation-1)' }}
          >
            <button
              type="button"
              data-testid="status-option-all"
              onClick={() => {
                onStatusFilterChange('')
                setStatusDropdownOpen(false)
              }}
              className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
            >
              All
            </button>
            {statusOptions.map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`status-option-${s}`}
                onClick={() => {
                  onStatusFilterChange(s)
                  setStatusDropdownOpen(false)
                }}
                className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
